import { SingleDatePicker } from "@/components/SingleDatePicker";
import { TimePicker } from "@/components/TimePicker";
import { type WidgetSettingsDefinition } from "@repo/api";
import {
  Button,
  FormField,
  ListViewItem,
  Text,
  calendarIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ModalSheet,
  SectionContainer,
  TwoColumnElementsContainer,
  localizeDateTimeRange,
  translateKey,
} from "@repo/ui";
import { combineDates, validateToTime } from "@repo/utils";
import { endOfDay, isBefore, startOfDay } from "date-fns";
import { useEffect, useState } from "react";

const getDefaultDateRange = (
  setting: Record<string, any>,
  settingDefinition: WidgetSettingsDefinition
) => {
  const hasDefault =
    Array.isArray(settingDefinition.defaultValue) &&
    settingDefinition.defaultValue.length === 2;

  const value =
    setting[settingDefinition.id] ??
    (hasDefault ? settingDefinition.defaultValue : undefined);

  const displayStart = value?.[0] ? new Date(value[0]) : undefined;
  const displayEnd = value?.[1] ? new Date(value[1]) : undefined;

  return { value, displayStart, displayEnd };
};

export const DateRangeSettingsControl = ({
  settingDefinition,
  setting,
  setSetting,
}: {
  settingDefinition: WidgetSettingsDefinition;
  setting: Record<string, any>;
  setSetting: (newSetting: Record<string, object>) => void;
}) => {
  const { value, displayStart, displayEnd } = getDefaultDateRange(
    setting,
    settingDefinition
  );

  const [startDate, setStartDate] = useState<Date | undefined>(displayStart);
  const [startTime, setStartTime] = useState<Date | undefined>(displayStart);
  const [endDate, setEndDate] = useState<Date | undefined>(displayEnd);
  const [endTime, setEndTime] = useState<Date | undefined>(displayEnd);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  useEffect(() => {
    setStartDate(displayStart);
    setStartTime(displayStart);
    setEndDate(displayEnd);
    setEndTime(displayEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validDate =
    startDate &&
    endDate &&
    startTime &&
    endTime &&
    isBefore(startDate, endDate) &&
    isBefore(startTime, endTime);

  const rangeString =
    displayStart && displayEnd
      ? localizeDateTimeRange(displayStart, displayEnd)
      : undefined;

  const onSave = () => {
    if (
      startDate &&
      startTime &&
      endDate &&
      endTime &&
      isBefore(startDate, endDate) &&
      isBefore(startTime, endTime)
    ) {
      const start = combineDates(startDate, startTime);
      const end = combineDates(endDate, endTime);

      setSetting({
        ...setting,
        [settingDefinition.id]: [start, end],
      });
    }

    setModalIsOpen(false);
  };

  return (
    <>
      <ListViewItem
        control="navigational"
        title={translateKey(settingDefinition.title)}
        description={rangeString}
        onClick={() => setModalIsOpen(true)}
        leadingIcon={calendarIcon}
      />
      <ModalSheet
        title={translateKey(settingDefinition.title)}
        visible={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onBack={() => setModalIsOpen(false)}
        footer={
          <Button
            type="primary"
            text={t("action.confirm")}
            onClick={onSave}
            disabled={!validDate}
          />
        }
      >
        <SectionContainer>
          <div>
            <Text textStyle="Heading.XS">{t("label.from")}</Text>
            <TwoColumnElementsContainer>
              <FormField
                shouldValidate={!startDate}
                valid={Boolean(startDate)}
                validation={{
                  msg: t("prompt.invalid_date"),
                  id: "error",
                  type: "error",
                }}
              >
                <SingleDatePicker
                  id={`${settingDefinition.id}-start-date`}
                  label={t("label.date")}
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    setStartTime(startOfDay(value));
                  }}
                />
              </FormField>
              <TimePicker
                id={`${settingDefinition.id}-start-time`}
                label={t("label.time")}
                value={startTime}
                onChange={setStartTime}
                timeIntervals={30}
              />
            </TwoColumnElementsContainer>
          </div>
          <div>
            <Text textStyle="Heading.XS">{t("label.to")}</Text>
            <TwoColumnElementsContainer>
              <FormField
                shouldValidate={!endDate}
                valid={Boolean(endDate)}
                validation={{
                  msg: t("prompt.invalid_date"),
                  id: "error",
                  type: "error",
                }}
              >
                <SingleDatePicker
                  id={`${settingDefinition.id}-end-date`}
                  label={t("label.date")}
                  value={endDate}
                  onChange={(value) => {
                    setEndDate(value);
                    setEndTime(endOfDay(value));
                  }}
                  minDate={startDate}
                />
              </FormField>
              <TimePicker
                id={`${settingDefinition.id}-end-time`}
                label={t("label.time")}
                value={endTime}
                onChange={setEndTime}
                timeIntervals={30}
                minTime={startTime}
                isValid={validateToTime(startDate, startTime, endDate, endTime)}
              />
            </TwoColumnElementsContainer>
          </div>
        </SectionContainer>
      </ModalSheet>
    </>
  );
};
